import React, { ReactNode, useCallback, useState } from 'react';
import { Box } from '@meddihub/ui';
import type {
	MzcrLocale,
	PickedHealthDocument,
	SaveToHealthDocumentationRequest,
	SaveToHealthDocumentationResult,
} from '@meddihub/mzcr-ui';
import { MzcrContext } from '../Services/mzcrApi';
import useMzcrPatientService from '../Services/useMzcrPatientService';
import useMzcrSettingsLink from '../Services/useMzcrSettingsLink';
import useRefreshMzcrAcl from '../Services/useRefreshMzcrAcl';
import useMzcrLocale from '../Services/useMzcrLocale';
import SaveToHealthDocumentationModal from './SaveToHealthDocumentationModal';
import PickFromHealthDocumentationModal from './PickFromHealthDocumentationModal';
import MzcrPatientStateView from './MzcrPatientStateView';
import ScrollContainer from '../../Base/Components/ScrollContainer';

export interface MzcrPatientServiceRenderProps extends MzcrContext {
	rid: string;
	/** Opens the workplace settings on the MZČR tab – only for users who may manage the workplace. */
	onOpenCertificateSettings?: () => void;
	/** Lets the user pick a folder of the patient's health documentation and stores the files there. */
	onSaveToHealthDocumentation: (
		request: SaveToHealthDocumentationRequest,
	) => Promise<SaveToHealthDocumentationResult>;
	/** Lets the user pick files from the patient's health documentation. */
	onPickFromHealthDocumentation: () => Promise<PickedHealthDocument[] | null>;
	/** Reloads the permissions once the workplace certificate was uploaded in place of the service. */
	onCertificateUploaded: () => void;
	/** Language of the service – follows the language of the app. */
	locale: MzcrLocale;
}

interface Props {
	patientId: string;
	/** Switches the patient card to the Profile tab (to fix the patient's name or insurance number). */
	onOpenProfile?: () => void;
	children: (props: MzcrPatientServiceRenderProps) => ReactNode;
}

/**
 * Renders the MZČR service of the patient once the workplace context and the patient's RID are
 * known (`useMzcrPatientService`). Everything else is shown as a state of the tab
 * (`MzcrPatientStateView`). Also hosts the dialogs the service opens in the health documentation.
 */
const MzcrPatientService: React.FC<Props> = ({
	patientId,
	onOpenProfile,
	children,
}) => {
	const { state, isBusy, lookupRid, retry } = useMzcrPatientService(patientId);
	const { onOpenCertificateSettings } = useMzcrSettingsLink();
	const refreshMzcrAcl = useRefreshMzcrAcl();
	const locale = useMzcrLocale();
	// Open host dialogs - each resolves the promise the MZČR service waits on
	const [saveRequest, setSaveRequest] = useState<{
		request: SaveToHealthDocumentationRequest;
		resolve: (result: SaveToHealthDocumentationResult) => void;
	} | null>(null);
	const [pickRequest, setPickRequest] = useState<{
		resolve: (documents: PickedHealthDocument[] | null) => void;
	} | null>(null);

	const onSaveToHealthDocumentation = useCallback(
		(request: SaveToHealthDocumentationRequest) =>
			new Promise<SaveToHealthDocumentationResult>(resolve =>
				setSaveRequest({ request, resolve }),
			),
		[],
	);
	const onPickFromHealthDocumentation = useCallback(
		() =>
			new Promise<PickedHealthDocument[] | null>(resolve =>
				setPickRequest({ resolve }),
			),
		[],
	);

	// The patient card tab has a fixed height - the service scrolls inside it
	return (
		<ScrollContainer>
			<Box px={6} py={state.status === 'ready' ? 6 : 8}>
				{state.status === 'ready' ? (
					<>
						{children({
							...state.context,
							rid: state.rid,
							onOpenCertificateSettings,
							onSaveToHealthDocumentation,
							onPickFromHealthDocumentation,
							onCertificateUploaded: refreshMzcrAcl,
							locale,
						})}
						{saveRequest && (
							<SaveToHealthDocumentationModal
								patientId={patientId}
								request={saveRequest.request}
								onClose={saved => {
									saveRequest.resolve(saved);
									setSaveRequest(null);
								}}
							/>
						)}
						{pickRequest && (
							<PickFromHealthDocumentationModal
								patientId={patientId}
								onClose={documents => {
									pickRequest.resolve(documents);
									setPickRequest(null);
								}}
							/>
						)}
					</>
				) : (
					<MzcrPatientStateView
						state={state}
						isBusy={isBusy}
						onRetry={retry}
						onLookupRid={lookupRid}
						onOpenProfile={onOpenProfile}
						onOpenCertificateSettings={onOpenCertificateSettings}
					/>
				)}
			</Box>
		</ScrollContainer>
	);
};

export default MzcrPatientService;
